import { useSyncExternalStore } from "react";

export const IMAGE_WORK_ROLLOUT_CONTRACT_VERSION = "image-work-rollout-v1";

const STATIC_CONFIG = Object.freeze({
  enabled: process.env.REACT_APP_IMAGE_WORK_CONTROL_ENABLED,
  forceNative: process.env.REACT_APP_IMAGE_WORK_CONTROL_FORCE_NATIVE,
  percentage: process.env.REACT_APP_IMAGE_WORK_CONTROL_ROLLOUT_PERCENT,
  rolloutStage: process.env.REACT_APP_IMAGE_WORK_CONTROL_ROLLOUT_STAGE,
  monitoringEnabled: process.env.REACT_APP_IMAGE_WORK_CONTROL_MONITORING_ENABLED,
});

export const DEFAULT_IMAGE_WORK_ROLLOUT = Object.freeze({
  contractVersion: IMAGE_WORK_ROLLOUT_CONTRACT_VERSION,
  enabled: true,
  forceNative: false,
  percentage: 100,
  rolloutStage: "full",
  monitoringEnabled: true,
  seed: "image-work-v1",
});

const normalizeBoolean = (value, fallback) => {
  if (value === undefined || value === null || value === "") return Boolean(fallback);
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off"].includes(normalized)) return false;
  }
  return Boolean(value);
};

const normalizePercentage = (value, fallback = 100) => {
  if (value === undefined || value === null || value === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.min(100, Math.max(0, Math.round(parsed))) : fallback;
};

export const normalizeImageWorkRolloutConfig = (value = {}) => ({
  contractVersion: IMAGE_WORK_ROLLOUT_CONTRACT_VERSION,
  enabled: normalizeBoolean(value.enabled, DEFAULT_IMAGE_WORK_ROLLOUT.enabled),
  forceNative: normalizeBoolean(value.forceNative, DEFAULT_IMAGE_WORK_ROLLOUT.forceNative),
  percentage: normalizePercentage(value.percentage, DEFAULT_IMAGE_WORK_ROLLOUT.percentage),
  rolloutStage: String(value.rolloutStage || DEFAULT_IMAGE_WORK_ROLLOUT.rolloutStage)
    .trim().slice(0, 32) || DEFAULT_IMAGE_WORK_ROLLOUT.rolloutStage,
  monitoringEnabled: normalizeBoolean(
    value.monitoringEnabled,
    DEFAULT_IMAGE_WORK_ROLLOUT.monitoringEnabled,
  ),
  seed: String(value.seed || DEFAULT_IMAGE_WORK_ROLLOUT.seed).trim().slice(0, 64)
    || DEFAULT_IMAGE_WORK_ROLLOUT.seed,
});

export const hashImageWorkRolloutKey = (value = "") => {
  const input = String(value || "");
  let hash = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

let fallbackCohortKey = `page_${Math.random().toString(36).slice(2, 12)}`;

const getCohortKey = () => {
  if (typeof window === "undefined" || !window.sessionStorage) return fallbackCohortKey;
  try {
    const storageKey = "ecotour:image-work-cohort:v1";
    const existing = window.sessionStorage.getItem(storageKey);
    if (existing) return existing;
    fallbackCohortKey = window.crypto?.randomUUID?.() || fallbackCohortKey;
    window.sessionStorage.setItem(storageKey, fallbackCohortKey);
    return fallbackCohortKey;
  } catch {
    return fallbackCohortKey;
  }
};

let runtimeConfig = {};
let configSnapshot = normalizeImageWorkRolloutConfig({ ...STATIC_CONFIG });
const listeners = new Set();

const rebuildSnapshot = () => {
  const next = normalizeImageWorkRolloutConfig({ ...STATIC_CONFIG, ...runtimeConfig });
  if (JSON.stringify(next) === JSON.stringify(configSnapshot)) return false;
  configSnapshot = next;
  listeners.forEach((listener) => listener());
  return true;
};

export const setRuntimeImageWorkRolloutConfig = (value = {}) => {
  runtimeConfig = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  rebuildSnapshot();
  return configSnapshot;
};

export const getImageWorkRolloutConfig = () => configSnapshot;

export const resolveImageWorkRollout = (config = configSnapshot) => {
  const normalized = normalizeImageWorkRolloutConfig(config);
  const bucket = hashImageWorkRolloutKey(`${normalized.seed}:${getCohortKey()}`) % 100;
  let controlEnabled = true;
  let reason = "included";
  if (!normalized.enabled) {
    controlEnabled = false;
    reason = "disabled";
  } else if (normalized.forceNative) {
    controlEnabled = false;
    reason = "force-native";
  } else if (normalized.percentage <= 0 || bucket >= normalized.percentage) {
    controlEnabled = false;
    reason = "percentage-excluded";
  }
  return Object.freeze({ ...normalized, bucket, controlEnabled, reason });
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useImageWorkRollout = () => {
  const current = useSyncExternalStore(subscribe, getImageWorkRolloutConfig, getImageWorkRolloutConfig);
  return resolveImageWorkRollout(current);
};

export const __resetImageWorkRolloutForTests = () => {
  runtimeConfig = {};
  fallbackCohortKey = "test-cohort";
  configSnapshot = normalizeImageWorkRolloutConfig({ ...STATIC_CONFIG });
  listeners.forEach((listener) => listener());
};

export default resolveImageWorkRollout;
